import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of } from 'rxjs';
import { LocalStorageService } from './storage/local-storage.service';
import {
    LEGACY_ENTERPRISE_ID_KEY,
    migrateLegacyEnterpriseId,
    SELECTED_ENTERPRISE_ID_KEY,
} from './storage/enterprise-storage';
import { environment } from '@env/environment';
import { EnterpriseUserAccessViewModel, UserProxy } from '../../proxy/proxies';
import { CropMapAccessService } from './cropmap-access.service';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { MapService } from '../map/map.service';
import { InquiryMapLayersService } from '../../features/inquiry/services/inquiry-map-layers.service';

export interface CropMapUserEnterpiseListViewModel {
    userId?: string;
    userName?: string;
    userSurname?: string;
    enterprises?: any[];
    [key: string]: any;
}

/** Options for change-enterprise (from GET /v4.0/crop-map-user/enterprise). */
export interface CropMapEnterpriseOption {
    enterpriseId: string;
    name?: string;
    hasCropMapAccess?: boolean;
    cropMapAccessRoleId?: string;
}

interface UserCache {
    currentUser: CropMapUserEnterpiseListViewModel | null;
    profile: CropMapUserEnterpiseListViewModel | null;
}

interface RegistrationCache {
    userId: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    viewAllUser: boolean = false;
    private initialized = false;

    private readonly userCache = new BehaviorSubject<UserCache>({
        currentUser: null,
        profile: null,
    });

    private readonly registrationCache = new BehaviorSubject<RegistrationCache>({
        userId: null,
    });

    private readonly cropMapEnterpriseOptionsSubject = new BehaviorSubject<CropMapEnterpriseOption[]>([]);
    private permissionEnterpriseIds: string[] = [];

    private readonly permissionService = inject(PermissionService);

    selectedEnterprise: EnterpriseUserAccessViewModel | null = null;
    private selectedEnterpriseSubject = new BehaviorSubject<EnterpriseUserAccessViewModel | null>(null);
    selectedEnterprise$ = this.selectedEnterpriseSubject.asObservable();
    get selectedEnterpriseId(): string {
        const fromMemory = this.selectedEnterprise?.id?.trim();
        if (fromMemory) return fromMemory;
        return this.localStorageService.getItem<string>(SELECTED_ENTERPRISE_ID_KEY)?.trim() ?? '';
    }

    private readonly _cropMapAccessService = inject(CropMapAccessService);
    private readonly _authService = inject(AuthService);
    private readonly _userProxy = inject(UserProxy);
    private readonly _mapService = inject(MapService);
    private readonly _inquiryMapLayersService = inject(InquiryMapLayersService);

    constructor(private localStorageService: LocalStorageService) {}

    markInitialized(): void {
        this.initialized = true;
    }

    private updateCache(data: Partial<UserCache>): void {
        const currentCache = this.userCache.value;
        const newCache: UserCache = {
            ...currentCache,
            ...data,
        };
        this.userCache.next(newCache);
    }

    clearCache(): void {
        this.initialized = false;
        this.viewAllUser = false;
        this.userCache.next({
            currentUser: null,
            profile: null,
        });
        this.cropMapEnterpriseOptionsSubject.next([]);
        this.permissionEnterpriseIds = [];
    }

    setPermissionEnterpriseIds(enterpriseIds: string[]): void {
        this.permissionEnterpriseIds = enterpriseIds.filter((id) => !!id);
    }

    /**
     * True when the user has at least one enterprise with CropMap access
     * ({@link CropMapEnterpriseOption.hasCropMapAccess} from API).
     */
    hasCropMapEnterpriseAccess(): boolean {
        return this.cropMapEnterpriseOptionsSubject.value.length > 0;
    }

    /** Populates enterprises from CropMapUserProxy.cropMapUser_GetCropMapEnterpriseByUser(). */
    setCropMapEnterpriseOptions(
        enterprises:
            | Pick<
                  EnterpriseUserAccessViewModel,
                  'id' | 'name' | 'hasCropMapAccess' | 'cropMapAccessRoles'
              >[]
            | undefined
    ): void {
        migrateLegacyEnterpriseId(this.localStorageService);

        const list: CropMapEnterpriseOption[] = (enterprises ?? [])
            .filter((e) => e.hasCropMapAccess === true)
            .map((e) => ({
                enterpriseId: e.id,
                name: e.name,
                hasCropMapAccess: e.hasCropMapAccess,
                cropMapAccessRoleId: e.cropMapAccessRoles?.[0]?.id,
            }));
        this.cropMapEnterpriseOptionsSubject.next(list);

        const withCropMapAccess = list.filter((e) => e.hasCropMapAccess === true);
        if (withCropMapAccess.length === 0) {
            return;
        }

        const currentId = this.localStorageService.getItem<string>(SELECTED_ENTERPRISE_ID_KEY);
        const currentIsValid =
            !!currentId && withCropMapAccess.some((e) => e.enterpriseId === currentId);

        if (!currentIsValid) {
            this.localStorageService.setItem(
                SELECTED_ENTERPRISE_ID_KEY,
                withCropMapAccess[0].enterpriseId,
            );
        }
    }

    setSelectedEnterprise(enterprise: EnterpriseUserAccessViewModel) {
        if (!enterprise?.id || !enterprise?.cropMapAccessRoles?.[0]?.id) {
            this.clearSelectedEnterprise();
            return;
        }
        this.selectedEnterprise = enterprise;
        this.selectedEnterpriseSubject.next(enterprise);
        this.localStorageService.setItem('selectedEnterprise', enterprise);
        this.localStorageService.setItem(SELECTED_ENTERPRISE_ID_KEY, enterprise.id);

        const userPermissions = this._authService.userInfo?.permissions ?? [];
        void this.permissionService.initializeUserPermissions(
            this.permissionEnterpriseIds,
            userPermissions,
        );
    }

    get cropMapEnterpriseOptions$(): Observable<CropMapEnterpriseOption[]> {
        return this.cropMapEnterpriseOptionsSubject.asObservable();
    }

    clearRegistrationCache(): void {
        this.registrationCache.next({
            userId: null,
        });
    }

    setCurrentUser(user: CropMapUserEnterpiseListViewModel | null): void {
        this.updateCache({ currentUser: user });
    }

    get registrationUserId$(): Observable<string | null> {
        return this.registrationCache.pipe(map((cache) => cache.userId));
    }

    get currentUser$(): Observable<CropMapUserEnterpiseListViewModel | null> {
        return this.userCache.pipe(map((cache) => cache.currentUser));
    }

    get userProfile$(): Observable<CropMapUserEnterpiseListViewModel | null> {
        return this.userCache.pipe(map((cache) => cache.profile));
    }

    get currentUser(): CropMapUserEnterpiseListViewModel | null {
        return this.userCache.value.currentUser;
    }

    get userProfile(): CropMapUserEnterpiseListViewModel | null {
        return this.userCache.value.profile;
    }

    getUserFullName(): string {
        const user = this.currentUser;
        if (!user?.userName) return '';
        return user.userName.trim();
    }

    getUserFirstName(): string {
        const user = this.currentUser;
        if (!user?.userName) return '';
        return user.userName || '';
    }

    isInitialized(): boolean {
        return this.initialized;
    }

    getAppId(): string {
        return environment.appId;
    }

    clearSelectedEnterprise(): void {
        this.selectedEnterprise = null;
        this.selectedEnterpriseSubject.next(null);
        this.localStorageService.removeItem('selectedEnterprise');
        this.localStorageService.removeItem(SELECTED_ENTERPRISE_ID_KEY);
        this.localStorageService.removeItem(LEGACY_ENTERPRISE_ID_KEY);
    }

    /** Clears in-memory user, enterprise, and feature singleton state (not auth tokens). */
    resetUserAndFeatureState(): void {
        this.clearCache();
        this.clearRegistrationCache();
        this.clearSelectedEnterprise();
        this._mapService.resetForLogout();
        this._inquiryMapLayersService.resetMap();
    }

    /** Full client logout after API sign-out (or when invoked from {@link AuthService.logOut}). */
    performLogout(): void {
        this.resetUserAndFeatureState();
        this._authService.finishLogout();
        this._authService.navigateToLogin();
    }

    /** Same teardown as {@link performLogout}, then reload (401 / expired session). */
    performAuthErrorReset(): void {
        this._authService.resetPlantationOnLogout();
        this.resetUserAndFeatureState();
        this._authService.finishLogout();
        window.location.reload();
    }

    logout(): void {
        this._userProxy
            .user_Logout()
            .pipe(finalize(() => this._authService.logOut()))
            .subscribe({ error: () => {} });
    }
}
